import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, FormControl, FormLabel, Grid, Heading, Input, Select, Textarea, Text, VStack, HStack, Flex, useDisclosure, FormErrorMessage } from "@chakra-ui/react";
import { ChevronLeft, Plus } from "lucide-react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import { useAccessCheck } from "@/hooks/useAccess";
import AddProcedureModal from "@/components/modals/AddProcedureModal";
import { COUNTRIES, getFlagUrl } from "@/lib/utils/countries";
import ProceduresTable from "@/components/tables/ProceduresTable";
import PrescriptionsTable from "@/components/tables/PrescriptionsTable";
import { procedureService } from "@/lib/services/procedureService";
import { Procedures } from "@/types/procedures";
import { CreateAppointmentData, Prescription } from "@/types/appointment";
import { userService } from "@/lib/services/userService";
import { DoctorOverview } from "@/types";
import { appointmentService } from "@/lib/services/appointmentService";
import { useAuthStore } from "@/store/auth";

interface FormState {
  patientID: string;
  countryCode: string;
  phoneNumber: string;
  date: string;
  time: string;
  primaryDoctor: string;
  appointmentType: string;
  notes: string;
}

interface FormErrors {
  patientID?: string;
  phoneNumber?: string;
  primaryDoctor?: string;
  appointmentType?: string;
  date?: string;
  time?: string;
}

interface DataState {
  procedures: Procedures[];
  prescriptions: Prescription[];
  availableProcedures: Procedures[];
  availableDoctors: any[];
  availablePatients: any[];
}

interface UIState {
  showCountryMenu: boolean;
  isProcessing: boolean;
  currentPage: number;
  prescriptionCurrentPage: number;
  editingProcedure: Procedures | null;
  editingPrescription: Prescription | null;
}

const INITIAL_FORM_STATE: FormState = {
  patientID: "",
  countryCode: "+63",
  phoneNumber: "",
  date: "",
  time: "",
  primaryDoctor: "",
  appointmentType: "",
  notes: "",
};

const INITIAL_DATA_STATE: DataState = {
  procedures: [],
  prescriptions: [],
  availableProcedures: [],
  availableDoctors: [],
  availablePatients: [],
};

const INITIAL_UI_STATE: UIState = {
  showCountryMenu: false,
  isProcessing: false,
  currentPage: 1,
  prescriptionCurrentPage: 1,
  editingProcedure: null,
  editingPrescription: null,
};

const ITEMS_PER_PAGE = 10;

const CreateAppointment = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const patientIdFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("patientId") || null;
  }, [location.search]);

  const { showError, showSuccess } = useToastNotification();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isPrescriptionOpen, onOpen: onPrescriptionOpen, onClose: onPrescriptionClose } = useDisclosure();
  const access = useAccessCheck();
  const role = useAuthStore((state) => state.user?.role);
  const user_id = useAuthStore((state) => state.user?.id);
  
  const isDoctor = role === "Doctor";

  const dateRef = useRef<HTMLInputElement>(null);
  const timeRef = useRef<HTMLInputElement>(null);
  const countryMenuRef = useRef<HTMLDivElement>(null);

  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);
  const [dataState, setDataState] = useState<DataState>(INITIAL_DATA_STATE);
  const [uiState, setUIState] = useState<UIState>(INITIAL_UI_STATE);
  const [errors, setErrors] = useState<FormErrors>({});

  const totalCost = useMemo(() => dataState.procedures.reduce((acc, proc) => acc + proc.price, 0), [dataState.procedures]);

  const paginatedProcedures = useMemo(() => dataState.procedures.slice((uiState.currentPage - 1) * ITEMS_PER_PAGE, uiState.currentPage * ITEMS_PER_PAGE), [dataState.procedures, uiState.currentPage]);

  const totalPages = useMemo(() => Math.ceil(dataState.procedures.length / ITEMS_PER_PAGE), [dataState.procedures.length]);

  const paginatedPrescriptions = useMemo(
    () => dataState.prescriptions.slice((uiState.prescriptionCurrentPage - 1) * ITEMS_PER_PAGE, uiState.prescriptionCurrentPage * ITEMS_PER_PAGE),
    [dataState.prescriptions, uiState.prescriptionCurrentPage],
  );

  const prescriptionTotalPages = useMemo(() => Math.ceil(dataState.prescriptions.length / ITEMS_PER_PAGE), [dataState.prescriptions.length]);

  const getTodayDate = useCallback(() => new Date().toISOString().split("T")[0], []);

  const updateFormField = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const updateUIState = useCallback(<K extends keyof UIState>(field: K, value: UIState[K]) => {
    setUIState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const clearError = useCallback((field: keyof FormErrors) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryMenuRef.current && !countryMenuRef.current.contains(event.target as Node)) {
        updateUIState("showCountryMenu", false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [updateUIState]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [fetchedProcedures, fetchedDoctors, fetchedPatients] = await Promise.all([
          procedureService.getProcedures(),
          userService.getDoctors(),
          userService.getPatients(),
        ]);

        setDataState({
          procedures: [],
          prescriptions: [],
          availableProcedures: fetchedProcedures || [],
          availableDoctors: fetchedDoctors || [],
          availablePatients: fetchedPatients || [],
        });

        if (isDoctor && user_id) {
          updateFormField("primaryDoctor", user_id.toString());
        }

        if (patientIdFromUrl) {
          const matchedPatient = (fetchedPatients || []).find(
            (p: any) => p.id.toString() === patientIdFromUrl.toString()
          );
          if (matchedPatient) {
            updateFormField("patientID", patientIdFromUrl);
            updateFormField("phoneNumber", matchedPatient.user_phone_number || "0");
          }
        }
      } catch (error) {
        showError("Failed to load initial data");
      }
    };

    fetchData();
  }, [showError, isDoctor, user_id, patientIdFromUrl]);

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formState.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    }

    if (!formState.primaryDoctor) {
      newErrors.primaryDoctor = "Primary doctor is required";
    }

    if (!formState.date) {
      newErrors.date = "Date is required";
    } else {
      const selectedDate = new Date(formState.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.date = "Date cannot be in the past";
      }
    }

    if (!formState.time) {
      newErrors.time = "Time is required";
    } else if (formState.date === getTodayDate()) {
      const [hours, minutes] = formState.time.split(":").map(Number);
      const selectedTime = new Date();
      selectedTime.setHours(hours, minutes, 0, 0);

      if (selectedTime < new Date()) {
        newErrors.time = "Time cannot be in the past";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState, getTodayDate]);

  const handlePatientChange = useCallback(
    (value: string) => {
      updateFormField("patientID", value);
      const patient = dataState.availablePatients?.find((p) => p.id.toString() === value.toString());
      if (patient) {
        updateFormField("phoneNumber", patient.user_phone_number || "0");
      }
      clearError("patientID");
    },
    [dataState.availablePatients, updateFormField, clearError],
  );

  const handleScheduleAppointment = useCallback(() => {
    updateUIState("editingProcedure", null);
    onOpen();
  }, [onOpen, updateUIState]);

  const handleSaveProcedure = useCallback(
    (procedure: Procedures) => {
      if (uiState.editingProcedure) {
        setDataState((prev) => ({
          ...prev,
          procedures: prev.procedures.map((p) => (p.id === uiState.editingProcedure!.id ? { ...p, ...procedure } : p)),
        }));
        showSuccess("Success", "Procedure updated successfully");
      } else {
        const generatedId = dataState.procedures.length > 0 ? Math.max(...dataState.procedures.map((p) => p.id || 0)) + 1 : 1;
        const { id: _omit, ...procedureWithoutId } = procedure as any;
        const newProcedure: Procedures = {
          ...procedureWithoutId,
          id: generatedId,
        };
        setDataState((prev) => ({
          ...prev,
          procedures: [...prev.procedures, newProcedure],
        }));
        showSuccess("Success", "Procedure added successfully");
      }
      updateUIState("editingProcedure", null);
    },
    [uiState.editingProcedure, dataState.procedures, showSuccess, updateUIState],
  );

  const handleEditProcedure = useCallback(
    (procedure: Procedures) => {
      updateUIState("editingProcedure", procedure);
      onOpen();
    },
    [onOpen, updateUIState],
  );

  const handleRemoveProcedure = useCallback(
    (procedureId: number) => {
      setDataState((prev) => ({
        ...prev,
        procedures: prev.procedures.filter((p) => p.id !== procedureId),
      }));
      showSuccess("Success", "Procedure removed successfully");

      const newTotalPages = Math.ceil((dataState.procedures.length - 1) / ITEMS_PER_PAGE);
      if (uiState.currentPage > newTotalPages && newTotalPages > 0) {
        updateUIState("currentPage", newTotalPages);
      }
    },
    [dataState.procedures.length, uiState.currentPage, showSuccess, updateUIState],
  );

  const handleConfirmSchedule = useCallback(async () => {
    if (!validateForm()) {
      showError("Please fill in all required fields correctly");
      return;
    }

    updateUIState("isProcessing", true);
    try {
      const newAppointment: CreateAppointmentData = {
        patient_id: formState.patientID,
        appointment_date: formState.date,
        appointment_time: formState.time,
        doctor_id: formState.primaryDoctor,
        appointment_type: formState.appointmentType,
        patient_phone_number: formState.phoneNumber,
        notes: formState.notes,
        procedures: dataState.procedures,
        prescriptions: dataState.prescriptions,
        procedures_id: dataState.procedures.map((p) => p.id || 0),
        total_cost: totalCost,
        status: "Scheduled",
      };

      await appointmentService.createAppointment(newAppointment);

      const existingAppointments = JSON.parse(localStorage.getItem("appointments") || "[]");
      localStorage.setItem("appointments", JSON.stringify([...existingAppointments, newAppointment]));

      showSuccess("Success", "Appointment scheduled successfully");
      setTimeout(() => navigate("/appointments"), 500);
    } catch (error: any) {
      showError("Failed to schedule appointment");
      updateUIState("isProcessing", false);
    }
  }, [validateForm, formState, dataState.procedures, dataState.prescriptions, totalCost, showError, showSuccess, navigate, updateUIState]);

  const handleAction = useCallback(
    (action: string) => {
      if (action === "cancel") {
        navigate("/appointments");
      } else if (action === "save") {
        handleConfirmSchedule();
      }
    },
    [navigate, handleConfirmSchedule],
  );

  const handleModalClose = useCallback(() => {
    updateUIState("editingProcedure", null);
    onClose();
  }, [onClose, updateUIState]);

  const handleAddPrescription = useCallback(() => {
    updateUIState("editingPrescription", null);
    onPrescriptionOpen();
  }, [onPrescriptionOpen, updateUIState]);

  const handleSavePrescription = useCallback(
    (prescription: { type: string; description: string; notes: string }) => {
      if (uiState.editingPrescription) {
        setDataState((prev) => ({
          ...prev,
          prescriptions: prev.prescriptions.map((p) => (p.id === uiState.editingPrescription!.id ? { ...p, ...prescription } : p)),
        }));
        showSuccess("Success", "Prescription updated successfully");
      } else {
        setDataState((prev) => {
          const generatedId = prev.prescriptions.length > 0 ? prev.prescriptions[prev.prescriptions.length - 1].id + 1 : 1;
          const newPrescription: Prescription = {
            id: generatedId,
            type: prescription.type,
            description: prescription.description,
            notes: prescription.notes,
          };
          console.log("Adding prescription:", newPrescription);
          console.log("Current prescriptions:", prev.prescriptions);
          console.log("New prescriptions array:", [...prev.prescriptions, newPrescription]);
          return {
            ...prev,
            prescriptions: [...prev.prescriptions, newPrescription],
          };
        });
        showSuccess("Success", "Prescription added successfully");
      }
      updateUIState("editingPrescription", null);
    },
    [uiState.editingPrescription, showSuccess, updateUIState],
  );

  const handleEditPrescription = useCallback(
    (prescription: Prescription) => {
      updateUIState("editingPrescription", prescription);
      onPrescriptionOpen();
    },
    [onPrescriptionOpen, updateUIState],
  );

  const handleRemovePrescription = useCallback(
    (prescriptionId: number) => {
      setDataState((prev) => ({
        ...prev,
        prescriptions: prev.prescriptions.filter((p) => p.id !== prescriptionId),
      }));
      showSuccess("Success", "Prescription removed successfully");

      const newTotalPages = Math.ceil((dataState.prescriptions.length - 1) / ITEMS_PER_PAGE);
      if (uiState.prescriptionCurrentPage > newTotalPages && newTotalPages > 0) {
        updateUIState("prescriptionCurrentPage", newTotalPages);
      }
    },
    [dataState.prescriptions.length, uiState.prescriptionCurrentPage, showSuccess, updateUIState],
  );

  const handlePrescriptionModalClose = useCallback(() => {
    updateUIState("editingPrescription", null);
    onPrescriptionClose();
  }, [onPrescriptionClose, updateUIState]);

  const renderPatientSection = () => (
    <>
      <Heading size={{ base: "sm", md: "md" }} mb={4} fontWeight="semibold">
        Patient Information
      </Heading>

      <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4}>
        <FormControl isInvalid={!!errors.patientID}>
          <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="400" color="#0A1B39">
            Patient Name{" "}
            <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <Select
            placeholder="Select patient"
            size={{ base: "sm", md: "md" }}
            fontSize={{ base: "xs", md: "sm" }}
            bg="white"
            color="#0A1B39"
            borderRadius="md"
            borderColor="gray.200"
            _hover={{ borderColor: "#DC3611" }}
            _focus={{ borderColor: "#DC3611", boxShadow: "0 0 0 1px #DC3611" }}
            value={formState.patientID}
            onChange={(e) => handlePatientChange(e.target.value)}
            isDisabled={!!patientIdFromUrl}
          >
            {dataState.availablePatients?.map((patient: DoctorOverview) => (
              <option key={patient.id} value={patient.id}>
                {patient.user_fullname}
              </option>
            ))}
          </Select>
          <FormErrorMessage fontSize="xs">{errors.patientID}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.phoneNumber}>
          <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="400" color="#0A1B39">
            Phone Number{" "}
            <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>

          <Box position="relative" ref={countryMenuRef}>
            <HStack spacing={0} onClick={() => updateUIState("showCountryMenu", !uiState.showCountryMenu)}>
              <Button
                width="60px"
                height={{ base: "32px", md: "40px" }}
                size={{ base: "sm", md: "md" }}
                bg="white"
                borderRadius="md"
                borderRightRadius="0"
                border="1px solid"
                borderColor="gray.200"
                _hover={{ borderColor: "#DC3611" }}
                _focus={{ borderColor: "#DC3611", boxShadow: "0 0 0 1px #DC3611" }}
                padding="0"
              >
                <Box as="img" src={getFlagUrl(formState.countryCode)} alt="flag" width="24px" height="24px" borderRadius="50%" />
              </Button>

              <Input
                color="#0A1B39"
                size={{ base: "sm", md: "md" }}
                placeholder="+63  123 1234 123"
                fontSize={{ base: "xs", md: "sm" }}
                bg="white"
                borderRadius="md"
                borderLeftRadius="0"
                borderLeft="0"
                borderColor="gray.200"
                _hover={{ borderColor: "#DC3611" }}
                _focus={{ borderColor: "#DC3611", boxShadow: "0 0 0 1px #DC3611" }}
                value={`${formState.countryCode}  ${formState.phoneNumber}`}
                onChange={(e) => {
                  const value = e.target.value.replace(`${formState.countryCode}  `, "");
                  updateFormField("phoneNumber", value);
                  clearError("phoneNumber");
                }}
              />
            </HStack>

            {uiState.showCountryMenu && (
              <Box
                position="absolute"
                top="100%"
                left="0"
                zIndex="1000"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                mt={1}
                maxH="300px"
                overflowY="auto"
                boxShadow="lg"
                width="100%"
              >
                {COUNTRIES.map((item) => (
                  <HStack
                    key={item.code}
                    p={2}
                    cursor="pointer"
                    _hover={{ bg: "#fbeaea" }}
                    onClick={() => {
                      updateFormField("countryCode", item.code);
                      updateUIState("showCountryMenu", false);
                    }}
                  >
                    <Box as="img" src={getFlagUrl(item.code)} alt={item.name} width="24px" height="18px" objectFit="cover" borderRadius="2px" />
                    <Text fontSize="sm">{item.name}</Text>
                    <Text fontSize="sm" color="gray.200" ml="auto">
                      {item.code}
                    </Text>
                  </HStack>
                ))}
              </Box>
            )}
          </Box>
          <FormErrorMessage fontSize="xs">{errors.phoneNumber}</FormErrorMessage>
        </FormControl>
      </Grid>
    </>
  );

  const renderAppointmentSection = () => (
    <>
      <Heading size={{ base: "sm", md: "md" }} mb={4} fontWeight="semibold">
        Appointment
      </Heading>

      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4}>
          <FormControl isInvalid={!!errors.date}>
            <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="400" color="#0A1B39">
              Date
            </FormLabel>
            <Input
              type="date"
              size={{ base: "sm", md: "md" }}
              fontSize={{ base: "xs", md: "sm" }}
              bg="white"
              borderRadius="md"
              borderColor="gray.200"
              _hover={{ borderColor: "#DC3611" }}
              _focus={{ borderColor: "#DC3611", boxShadow: "0 0 0 1px #DC3611" }}
              ref={dateRef}
              color="#0A1B39"
              value={formState.date}
              min={getTodayDate()}
              onClick={() => dateRef.current?.showPicker()}
              onChange={(e) => {
                updateFormField("date", e.target.value);
                clearError("date");
              }}
            />
            <FormErrorMessage fontSize="xs">{errors.date}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.time}>
            <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="400" color="#0A1B39">
              Time{" "}
              <Text as="span" color="red.500">
                *
              </Text>
            </FormLabel>
            <Input
              type="time"
              size={{ base: "sm", md: "md" }}
              fontSize={{ base: "xs", md: "sm" }}
              bg="white"
              borderRadius="md"
              borderColor="gray.200"
              _hover={{ borderColor: "#DC3611" }}
              _focus={{ borderColor: "#DC3611", boxShadow: "0 0 0 1px #DC3611" }}
              ref={timeRef}
              color="#0A1B39"
              value={formState.time}
              onClick={() => timeRef.current?.showPicker()}
              onChange={(e) => {
                updateFormField("time", e.target.value);
                clearError("time");
              }}
            />
            <FormErrorMessage fontSize="xs">{errors.time}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4}>
          <FormControl isInvalid={!!errors.primaryDoctor}>
            <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="400" color="#0A1B39">
              Primary Doctor{" "}
              <Text as="span" color="red.500">
                *
              </Text>
            </FormLabel>
            <Select
              placeholder="Select doctor"
              size={{ base: "sm", md: "md" }}
              fontSize={{ base: "xs", md: "sm" }}
              bg="white"
              borderRadius="md"
              borderColor="gray.200"
              _hover={{ borderColor: "#DC3611" }}
              _focus={{ borderColor: "#DC3611", boxShadow: "0 0 0 1px #DC3611" }}
              color="#0A1B39"
              value={formState.primaryDoctor}
              onChange={(e) => {
                updateFormField("primaryDoctor", e.target.value);
                clearError("primaryDoctor");
              }}
              isDisabled={isDoctor}
            >
              {dataState.availableDoctors?.map((doctor: DoctorOverview) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.user_fullname}{doctor.designation_id ? ` - ${doctor.designation_id.designation}` : ""}
                </option>
              ))}
            </Select>
            <FormErrorMessage fontSize="xs">{errors.primaryDoctor}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.appointmentType}>
            <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="400" color="#0A1B39">
              Appointment Type{" "}
              <Text as="span" color="red.500">
                *
              </Text>
            </FormLabel>
            <Select
              placeholder="Select type"
              size={{ base: "sm", md: "md" }}
              fontSize={{ base: "xs", md: "sm" }}
              bg="white"
              borderRadius="md"
              borderColor="gray.200"
              _hover={{ borderColor: "#DC3611" }}
              _focus={{ borderColor: "#DC3611", boxShadow: "0 0 0 1px #DC3611" }}
              color="#0A1B39"
              value={formState.appointmentType}
              onChange={(e) => {
                updateFormField("appointmentType", e.target.value);
                clearError("appointmentType");
              }}
            >
              <option value="Initial Appointment">Initial Appointment</option>
              <option value="Follow-up">Follow-up</option>
              <option value="Consultation">Consultation</option>
            </Select>
            <FormErrorMessage fontSize="xs">{errors.appointmentType}</FormErrorMessage>
          </FormControl>
        </Grid>

        <FormControl>
          <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="400" color="#0A1B39">
            Notes
          </FormLabel>
          <Textarea
            color="#0A1B39"
            fontSize={{ base: "sm", md: "sm" }}
            size={{ base: "sm", md: "md" }}
            placeholder="Type here..."
            bg="white"
            borderRadius="md"
            borderColor="gray.200"
            _hover={{ borderColor: "#DC3611" }}
            _focus={{ borderColor: "#DC3611", boxShadow: "0 0 0 1px #DC3611" }}
            value={formState.notes}
            onChange={(e) => updateFormField("notes", e.target.value)}
          />
        </FormControl>
      </VStack>
    </>
  );

  const renderProceduresSection = () => (
    <>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size={{ base: "sm", md: "md" }} fontWeight="semibold">
          Procedure
        </Heading>
        {access.can_edit && (
          <Button leftIcon={<Plus size={16} />} bg="#DC3611" _hover={{ bg: "#bf3c1fff" }} color="white" size="sm" fontSize={{ base: "xs", md: "sm" }} onClick={handleScheduleAppointment}>
            Add Procedure
          </Button>
        )}
      </Flex>

      <ProceduresTable
        procedures={dataState.procedures}
        paginatedProcedures={paginatedProcedures}
        currentPage={uiState.currentPage}
        totalPages={totalPages}
        itemsPerPage={ITEMS_PER_PAGE}
        setCurrentPage={(page) => updateUIState("currentPage", page)}
        canEdit={access.can_edit}
        onEdit={handleEditProcedure}
        onRemove={handleRemoveProcedure}
      />
    </>
  );

  const renderPrescriptionsSection = () => (
    <>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size={{ base: "sm", md: "md" }} fontWeight="semibold">
          Prescription
        </Heading>
        {access.can_edit && (
          <Button leftIcon={<Plus size={16} />} bg="#DC3611" _hover={{ bg: "#bf3c1fff" }} color="white" size="sm" fontSize={{ base: "xs", md: "sm" }} onClick={handleAddPrescription}>
            Add Prescription
          </Button>
        )}
      </Flex>

      <PrescriptionsTable
        prescriptions={dataState.prescriptions}
        paginatedPrescriptions={paginatedPrescriptions}
        currentPage={uiState.prescriptionCurrentPage}
        totalPages={prescriptionTotalPages}
        itemsPerPage={ITEMS_PER_PAGE}
        setCurrentPage={(page) => updateUIState("prescriptionCurrentPage", page)}
        canEdit={access.can_edit}
        onEdit={handleEditPrescription}
        onRemove={handleRemovePrescription}
      />
    </>
  );

  const renderActionButtons = () => (
    <HStack spacing={3} justify="flex-end" flexWrap="wrap">
      {access.can_edit && (
        <>
          <Button color="gray.900" fontWeight="500" bg="#F5F6F8" size={{ base: "sm", md: "sm" }} fontSize="13px" onClick={() => handleAction("cancel")} mt={-6}>
            Cancel
          </Button>
          <Button
            bg="#DC3611"
            _hover={{ bg: "#bf3c1fff" }}
            color="white"
            fontWeight="500"
            size={{ base: "sm", md: "sm" }}
            fontSize="13px"
            onClick={() => handleAction("save")}
            isLoading={uiState.isProcessing}
            loadingText="Scheduling..."
            mt={-6}
          >
            Schedule Appointment
          </Button>
        </>
      )}
    </HStack>
  );

  return (
    <AdminLayout>
      <Box p={4} h="100%" bg="#FEFCF8" overflow="auto" display="flex" flexDirection="column">
        <VStack spacing={4} align="stretch">
          {/* Header */}
          <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4}>
            <Box
              as="button"
              onClick={() => navigate(-1)}
              fontSize="sm"
              fontWeight="medium"
              display="flex"
              alignItems="center"
              gap={2}
              color="gray.600"
              _hover={{ color: "orange.500" }}
              transition="color 0.2s"
              cursor="pointer"
              bg="transparent"
              border="none"
              p={0}
              mb={{ base: 2, md: 4 }}
            >
              <ChevronLeft size={20} />
              Go Back
            </Box>
          </Box>

          {/* Main Content */}
          <VStack spacing={{ base: 4, md: 6 }} align="stretch">
            {renderPatientSection()}
            {renderAppointmentSection()}

            <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4} />

            {renderProceduresSection()}

            <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4} />

            <Heading size={{ base: "md", md: "lg" }} fontWeight="bold">
              Total Cost: ₱{totalCost.toLocaleString()}
            </Heading>

            <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4} />

            {renderActionButtons()}
          </VStack>
        </VStack>
      </Box>

      <AddProcedureModal isOpen={isOpen} onClose={handleModalClose} onSave={handleSaveProcedure} availableProcedures={dataState.availableProcedures} editingProcedure={uiState.editingProcedure} />
    </AdminLayout>
  );
};

export default CreateAppointment;
